import { ListRequest } from "@/common/common_utils";
import { X4bActivityStatus } from "@/services/signalr.service";
import { XpressVehicleType } from "./VehicleType";
import { Flag } from "@/modules/authentication/sign-up/components/CountryFlags";
import { X4bLocation } from "@/modules/concierge/views/PlacesRepository";
import { TripData, TripDriverState, X4bActivitySection } from "@/modules/concierge/engine/ConciergeCardViewModel";
import { BookingType, DeliveryOptions, DriverDto, PassengerDto, X4bActivityPointDto, XpressServiceType } from "./ActivityTypes";
import { ConciergeAwaitingCard, ConciergeCompletedCard, ConciergeNeedsAttentionCard, ConciergeRequestingCard, ConciergeScheduledCard } from "@/modules/concierge/cards/ConciergeCards";
import ConciergeCard, { CardInfoType } from "@/modules/concierge/cards/ConciergeCard";

export interface X4bActivityDto {
	Id: number;
	BookingId?: string;
	XpressId?: string;
	Date?: Date;
	Name?: string;
	Amount?: number;
	Fare?: number;
	VehicleType?: XpressVehicleType;
	TimeInSec?: number;
	TimeTillPickUpInSeconds?: number;
	TimeTillDropOffInSeconds?: number;
	RiderAssignedTime?: Date;
	PickedUpTime?: Date;
	CompletedTime?: Date;
	DistanceInM?: number;
	Status?: X4bActivityStatus;
	ActivitySection?: X4bActivitySection;
	BookingType?: BookingType;
	StatusName?: string;
	ProgramId?: number;
	ProgramName?: string;
	Location?: string;
	Pickup?: X4bActivityPointDto;
	Dropoff?: X4bActivityPointDto;
	DriverId?: number;
	Driver: DriverDto;
	CreatorName?: string;
	CreatorNameFull?: string;
	Memo?: string;
	Note?: string;
	Email?: string;
	Phone?: string;
	HeadPassenger: PassengerDto;
	AdditionalPassengers: PassengerDto[];
	SearchMatchType?: string; // "Name", "Location", or "Both" - indicates which field matched the search filter

	// Populated by the backend only for delivery/send orders (null for rides).
	DeliveryOptions?: DeliveryOptions;

	// Persisted service type; defaults to ride when omitted by the backend.
	ServiceTypeId?: XpressServiceType;
}

export class ActivityHelper {
	static isScheduled(act: X4bActivityDto) {
		if (act.Status === undefined) return false;
		return act.BookingType == BookingType.Scheduled && [X4bActivityStatus.New,
		X4bActivityStatus.RiderAccept, X4bActivityStatus.RiderAcceptB2B].includes(act.Status);
	}
	static isRequesting(act: X4bActivityDto) {
		if (act.Status === undefined) return false;
		return [X4bActivityStatus.New,
		X4bActivityStatus.RiderAssigning].includes(act.Status);
	}
	static isAwaitingPickup(act: X4bActivityDto) {
		if (act.Status === undefined) return false;
		return [X4bActivityStatus.RiderAssigned,
		X4bActivityStatus.ArrivedToPickup].includes(act.Status);
	}
	static isInProgress(act: X4bActivityDto) {
		if (act.Status === undefined) return false;
		return [X4bActivityStatus.DepartedToPickup,
		X4bActivityStatus.PickedUp,
		X4bActivityStatus.ArrivedToDrop].includes(act.Status);
	}
	static isCompleted(act: X4bActivityDto) {
		if (act.Status === undefined) return false;
		return [X4bActivityStatus.Completed].includes(act.Status);
	}
	static isCanceled(act: X4bActivityDto) {
		if (act.Status === undefined) return false;
		return [X4bActivityStatus.Canceled,
		X4bActivityStatus.Rejected,
		X4bActivityStatus.Expired].includes(act.Status);
	}

	// A send/delivery order is identified by its persisted service type.
	static isDelivery(act: X4bActivityDto): boolean {
		return act.ServiceTypeId === XpressServiceType.Delivery;
	}

	static isDriverAssigned(act: X4bActivityDto): boolean {
		const status = act.Status;
		return status === X4bActivityStatus.RiderAssigned ||
			status === X4bActivityStatus.RiderAccept ||
			status === X4bActivityStatus.RiderAcceptB2B ||
			status === X4bActivityStatus.DepartedToPickup ||
			status === X4bActivityStatus.ArrivedToPickup ||
			status === X4bActivityStatus.PickedUp ||
			status === X4bActivityStatus.ArrivedToDrop;
	}

	// TODO: move it to view or view model layer
	static getSectionName(label: X4bActivitySection): string {
		if (label == X4bActivitySection.TodaysActivity) {
			return '';
		} else if (label == X4bActivitySection.NeedsAttention) {
			return 'Needs Attention';
		} else if (label == X4bActivitySection.Requesting) {
			return 'Requesting';
		} else if (label == X4bActivitySection.AwaitingPickup) {
			return 'Awaiting Pickup';
		} else if (label == X4bActivitySection.InProgress) {
			return 'In Progress';
		} else if (label == X4bActivitySection.Completed) {
			return 'Completed';
		} else if (label == X4bActivitySection.Canceled) {
			return 'Cancelled';
		} else if (label == X4bActivitySection.Scheduled) {
			return 'Scheduled';
		} else if (label == X4bActivitySection.PastActivity) {
			return 'Past Activity';
		} else {
			throw new Error("Unexpected activity section");
		}
	}

	// TODO: move it to view or view model layer
	static getActivityCardExtraProps(a: X4bActivityDto, refetch: () => void): Record<string, any> | undefined {
		if (a.ActivitySection == X4bActivitySection.Canceled) {
			return { invalidateParent: refetch };
		}
		return undefined;
	}
}
